/**
 * Native chrome + lifecycle helpers. One file so call sites stay
 * platform-agnostic — every function here is a safe no-op on web.
 *
 * Use the high-level entry points:
 *   - initNativeChrome()    — call once at app boot from main.tsx
 *   - haptics.{light,medium,heavy,success,warning,error}
 *   - subscribeNetworkStatus(cb)
 *   - subscribeAppState(cb)
 */

import { App, type AppState } from '@capacitor/app';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Keyboard } from '@capacitor/keyboard';
import { Network, type ConnectionStatus } from '@capacitor/network';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style as StatusBarStyle } from '@capacitor/status-bar';
import { logger } from './logger';
import { platform } from './platform';

/**
 * Initialise status bar + keyboard handling and hide the splash screen.
 * Wrapped in try/catch because plugin failures should never block boot.
 */
export async function initNativeChrome(): Promise<void> {
  if (!platform.isNative) return;

  try {
    // Dark glyphs on the app's cream background. Capacitor's enum is
    // counter-intuitive — `Style.Light` means "dark text for light
    // backgrounds" and `Style.Dark` means "light text for dark
    // backgrounds." We want dark text, so `Style.Light`. The old code
    // used `Style.Dark` and rendered the status bar as invisible white-
    // on-white. Android needs an explicit bg color too — the config in
    // capacitor.config.ts is read at init but can be clobbered by the
    // system; setting it here makes the runtime value authoritative.
    await StatusBar.setStyle({ style: StatusBarStyle.Light });
    if (platform.isAndroid) {
      await StatusBar.setBackgroundColor({ color: '#f0eee9' });
    }
  } catch (err) {
    logger.warn('StatusBar init failed', { err: String(err) });
  }

  try {
    await SplashScreen.hide({ fadeOutDuration: 200 });
  } catch (err) {
    logger.warn('SplashScreen hide failed', { err: String(err) });
  }

  try {
    // Keyboard accessory bar nudges layout — let CSS safe-area handle it instead.
    if (platform.isIOS) {
      await Keyboard.setAccessoryBarVisible({ isVisible: false });
    }
  } catch (err) {
    logger.warn('Keyboard init failed', { err: String(err) });
  }
}

// ── Haptics ─────────────────────────────────────────────────────────────
// Tiny wrappers so call sites can fire feedback without importing Capacitor
// or worrying about web fallbacks.
export const haptics = {
  light: () => fire(() => Haptics.impact({ style: ImpactStyle.Light })),
  medium: () => fire(() => Haptics.impact({ style: ImpactStyle.Medium })),
  heavy: () => fire(() => Haptics.impact({ style: ImpactStyle.Heavy })),
  success: () => fire(() => Haptics.notification({ type: NotificationType.Success })),
  warning: () => fire(() => Haptics.notification({ type: NotificationType.Warning })),
  error: () => fire(() => Haptics.notification({ type: NotificationType.Error })),
  selection: () => fire(() => Haptics.selectionStart().then(() => Haptics.selectionEnd())),
};

function fire(action: () => Promise<unknown>): void {
  if (!platform.isNative) return;
  action().catch(() => {
    /* haptics are non-critical — swallow */
  });
}

// ── Network ─────────────────────────────────────────────────────────────
export type NetworkStatus = ConnectionStatus;

export async function getNetworkStatus(): Promise<NetworkStatus> {
  if (!platform.isNative) {
    return { connected: navigator.onLine, connectionType: 'unknown' };
  }
  return Network.getStatus();
}

/** Subscribe to network changes. Returns an unsubscribe fn. */
export function subscribeNetworkStatus(cb: (status: NetworkStatus) => void): () => void {
  if (!platform.isNative) {
    const onChange = () =>
      cb({ connected: navigator.onLine, connectionType: 'unknown' });
    window.addEventListener('online', onChange);
    window.addEventListener('offline', onChange);
    return () => {
      window.removeEventListener('online', onChange);
      window.removeEventListener('offline', onChange);
    };
  }
  const handle = Network.addListener('networkStatusChange', cb);
  return () => {
    void handle.then((h) => h.remove());
  };
}

// ── App lifecycle ───────────────────────────────────────────────────────
/** Subscribe to foreground/background transitions. Returns an unsubscribe fn. */
export function subscribeAppState(cb: (state: AppState) => void): () => void {
  if (!platform.isNative) {
    const onVisibility = () =>
      cb({ isActive: document.visibilityState === 'visible' });
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }
  const handle = App.addListener('appStateChange', cb);
  return () => {
    void handle.then((h) => h.remove());
  };
}

/** Subscribe to Android hardware back-button presses. No-op on iOS / web. */
export function subscribeBackButton(cb: () => void): () => void {
  if (!platform.isAndroid) return () => undefined;
  const handle = App.addListener('backButton', cb);
  return () => {
    void handle.then((h) => h.remove());
  };
}
